import { InputNumber as InputNumberAntd, InputNumberProps, Tooltip } from 'antd';
import 'antd/es/input-number/style/index';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import InputNumberOld, { IInputNumberOldProps } from './old';
import useMouseWheel from './useMouseWheel';
import './index.less';

interface IInputNumberProps extends Omit<InputNumberProps<number>, 'onChange'> {
    /**
     * 是否展示上下箭头
     */
    showStepBtn?: boolean;
    /** 启动鼠标滚动 */
    enableMouseWheel?: boolean;
    /** 鼠标滚动变化率(不支持状态更新) */
    mouseWheelRatio?: number;
    tip?: string;
    value?: number | null;
    keyboard?: boolean;
    /** 带有后缀图标的input (antd5.20.0以上版本才有, 这里用的antd版本太低, 自己实现) */
    suffix?: React.ReactNode;
    /** 带有前缀图标的input */
    beforeSuffix?: React.ReactNode;
    /**
     * antd的value是支持undefined/null, 但是saas-ui的这个组件不支持, 加这个参数, 让它支持一下.
     * 因为有些场景不需要默认值, 需要保留undefined/null
     */
    shouldReserveValueUndefinedOrNull?: boolean;
    /** 输入框输入过程中, 鼠标滚轮过程中, 增减号点击时的回调 */
    onChange?: (value: number | null, isMouseWheel: boolean) => void;
    /** 失焦时如果值有变化, 或鼠标滚轮结束后, 增减号点击时的回调 */
    onChangeAfter?: (value: number | null) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
    onMouseWheelEnd?: () => void;
}

/**
 * 判断是否是平板设备（SSR 安全）
 */
const isPad = () => {
    // SSR 环境（无 window/navigator/screen）
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || typeof screen === 'undefined') {
        return true; // SSR 下默认返回 true
    }
    const ua = navigator.userAgent.toLowerCase();
    const maxTouch = navigator.maxTouchPoints || 0;
    const dpr = window.devicePixelRatio || 1;
    const width = Math.min(window.screen.width, window.screen.height);

    // 1. iPad / iPadOS（iPadOS 13+ 会伪装成 Mac）
    if (/ipad/.test(ua) || (/macintosh/.test(ua) && maxTouch > 1)) {
        return true;
    }

    // 2. Android 平板（非手机）
    if (/android/.test(ua) && !/mobile/.test(ua)) {
        return true;
    }

    // 3. Windows / Linux / X11 平板（触屏设备）
    if ((/windows/.test(ua) || /linux/.test(ua) || /x11/.test(ua)) && maxTouch > 1) {
        // 增加 DPI 判断：一般平板 >= 1.5，触屏电脑多为 1.0~1.25
        const isHighDPI = dpr >= 1.5;
        // 宽度小于 1280 且高 DPI 视作平板，否则视作触屏电脑
        return width < 1280 && isHighDPI;
    }

    return false;
};

const DEBOUNCE_TIME = 300;

const InputNumberV2: React.FC<IInputNumberProps> = ({
    showStepBtn = true,
    enableMouseWheel = true,
    mouseWheelRatio = 1,
    tip,
    max: maxProp,
    min: minProp,
    value = null,
    precision,
    style = {},
    onChange,
    onChangeAfter,
    onMouseWheelEnd,
    keyboard = true,
    suffix,
    beforeSuffix,
    shouldReserveValueUndefinedOrNull = false,
    disabled = false,
    onFocus,
    onBlur,
    ...resetProps
}) => {
    const max = maxProp ?? Number.MAX_SAFE_INTEGER;
    const min = minProp ?? Number.MIN_SAFE_INTEGER;
    const onInputTimerRef = useRef<{ timer: number; callback: () => void } | null>(null);
    const wrapperElRef = useRef<HTMLDivElement>(null);
    const inputElRef = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState(false);
    const inputInfoRef = useRef<{
        // 聚焦时记录下来, 为了触发onChangeAfter (为什么不直接在onBlur时对比值是否改变? 因为: 例如100 -> 200 -> 100的场景, 会有触发了onChange但是onChangeAfter没有触发的bug)
        shouldTriggerOnChangeAfter: boolean;
        lastInputValue: number | null;
    }>({
        shouldTriggerOnChangeAfter: false,
        lastInputValue: null,
    });

    if (!shouldReserveValueUndefinedOrNull && value === null) {
        value = 1;
    }

    const checkValue = (num: number) => {
        // 取区间值
        let val = Math.min(num, max);
        val = Math.max(val, min);
        return val;
    };

    // 处理输入框或快捷键
    const handleAddOrMinus = (mode: 'add' | 'minus', by: 'keyboard' | 'btn') => {
        if (disabled) return;
        if (shouldReserveValueUndefinedOrNull && value === null) {
            onChange?.(minProp ?? 1, false);
            by === 'btn' && onChangeAfter?.(minProp ?? 1);
            return;
        }

        const diff = mode === 'add' ? 1 : -1;
        const val = checkValue((value as number) + diff);
        onChange?.(val, false);
        by === 'btn' && onChangeAfter?.(val);
    };

    type IOnChangeResultByEvent = { resultValue: number | null; shouldIgnore: boolean };

    const getOnChangeResultByEvent = (newText: string): IOnChangeResultByEvent => {
        if (shouldReserveValueUndefinedOrNull && (newText === '' || isNaN(+newText))) {
            if (value !== null) {
                return { resultValue: null, shouldIgnore: false };
            }
            return { resultValue: null, shouldIgnore: true };
        }

        // 如果删除所有内容 就不更新change事件 其他逻辑不变
        if (newText === '') {
            return { resultValue: null, shouldIgnore: true };
        }

        let num = +newText;

        if (isNaN(num)) {
            num = value ?? 1;
        }

        let resultValue = checkValue(num);
        if (resultValue < min) {
            resultValue = min;
        }

        if (precision === undefined) {
            return { resultValue, shouldIgnore: false };
        }

        num = num ? num : 0;
        const val = checkValue(+num.toFixed(precision || 0));
        return { resultValue: val, shouldIgnore: false };
    };

    const handleOnInput = (newVal: string) => {
        if (onInputTimerRef.current) {
            clearTimeout(onInputTimerRef.current.timer);
        }

        const timerCallback = () => {
            clearTimeout(onInputTimerRef.current?.timer);
            onInputTimerRef.current = null;

            const { resultValue, shouldIgnore } = getOnChangeResultByEvent(newVal);

            if (!shouldIgnore) {
                onChange?.(resultValue, false);
                inputInfoRef.current = {
                    shouldTriggerOnChangeAfter: true,
                    lastInputValue: resultValue,
                };
            }
        };

        onInputTimerRef.current = {
            timer: setTimeout(timerCallback, DEBOUNCE_TIME) as unknown as number,
            callback: timerCallback,
        };
    };

    // 键盘操作
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!keyboard) {
            return;
        }
        if (e.keyCode === 13) {
            // 修复: 输入完快速让输入框失焦时, 没有触发onChangeAfter的bug
            if (onInputTimerRef.current) {
                onInputTimerRef.current.callback();
            }
            if (inputInfoRef.current.shouldTriggerOnChangeAfter) {
                onChangeAfter?.(inputInfoRef.current.lastInputValue);
            }
            return;
        }
        inputInfoRef.current = {
            shouldTriggerOnChangeAfter: false,
            lastInputValue: null,
        };
        if (e.keyCode === 38) {
            handleAddOrMinus('add', 'keyboard');
        } else if (e.keyCode === 40) {
            handleAddOrMinus('minus', 'keyboard');
        }
    };

    if (!shouldReserveValueUndefinedOrNull) {
        value = value ? value : 0;
    }

    const mouseWheelHook = useMouseWheel({
        wrapperElRef,
        inputElRef,
        max,
        min,
        inputValue: value ?? 1,
        onChangeInputValue: (value) => onChange?.(value, true),
        onChangeAfter,
        // 在 iPad 设备上禁用鼠标滚动
        enableMouseWheel: isPad() ? false : enableMouseWheel,
        onMouseWheelEnd,
        mouseWheelRatio,
        disabled,
    });

    // 用于修改`组件销毁导致onBlur没有触发`的问题
    const shouldTriggerBlurRef = useRef(false);
    const hasTriggerBlurRef = useRef(false);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        // 修复: 输入完快速让输入框失焦时, 没有触发onChangeAfter的bug
        if (onInputTimerRef.current) {
            onInputTimerRef.current.callback();
        }

        mouseWheelHook.onInputBlur();
        setFocus(false);
        onBlur?.(e);
        if (inputInfoRef.current.shouldTriggerOnChangeAfter) {
            onChangeAfter?.(inputInfoRef.current.lastInputValue);
        }
    };

    // 修复因组件销毁导致没有触发onBlur的问题
    useEffect(() => {
        return () => {
            if (!hasTriggerBlurRef.current) {
                const fakeEvent = {
                    currentTarget: {
                        value: inputElRef.current?.value,
                    },
                    target: {
                        value: inputElRef.current?.value,
                    },
                } as any as React.FocusEvent<HTMLInputElement, Element>;
                handleBlur(fakeEvent);
            }
        };
    }, []);

    const renderEle = () => {
        return (
            <div
                ref={wrapperElRef}
                className={classnames('mt-input-number', {
                    'mt-input-number--focused': focus,
                    'mt-input-number--disabled': disabled,
                })}
                style={style}
                onClick={mouseWheelHook.onWrapperClick}
                onMouseDown={mouseWheelHook.onWrapperMouseDown}
                onTouchStart={mouseWheelHook.onWrapperTouchStart}
            >
                {!!beforeSuffix && <div className="input-addon-before">{beforeSuffix}</div>}
                <InputNumberAntd
                    ref={inputElRef}
                    value={
                        shouldReserveValueUndefinedOrNull
                            ? value
                            : checkValue(+(value as number).toFixed(precision || 0))
                    }
                    max={max}
                    min={min}
                    bordered={false}
                    stringMode
                    keyboard={keyboard}
                    disabled={disabled}
                    {...resetProps}
                    className="m-input-container"
                    onInput={handleOnInput}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => {
                        setFocus(true);
                        e.target.select();
                        onFocus?.(e);
                        shouldTriggerBlurRef.current = true;
                        inputInfoRef.current.lastInputValue = value;
                    }}
                    onBlur={handleBlur}
                />
                <div className="input-addon-after" onClick={(e) => e.stopPropagation()}>
                    {!!suffix && <div className="input-suffix">{suffix}</div>}
                    {showStepBtn && (
                        <div className={classnames('arrows', disabled && 'disabled')}>
                            <div className="top" onClick={() => handleAddOrMinus('add', 'btn')}>
                                <IconFont fontSize={10} name="ChevronUpBold" />
                            </div>
                            <div className="bottom" onClick={() => handleAddOrMinus('minus', 'btn')}>
                                <IconFont fontSize={10} name="ChevronDownBold" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return <Tooltip title={tip || ''}>{renderEle()}</Tooltip>;
};

/** @deprecated 旧版本的onChangeAfter时机不对, 请替换成v2 (如果有使用到onChangeAfter, 替换后可能会影响业务使用, 需要修改业务逻辑). v2用法: <InputNumber v2 ... /> */
function InputNumber(props: IInputNumberOldProps): React.ReactElement;
function InputNumber({ v2, ...props }: IInputNumberProps & { v2: true }): React.ReactElement;
function InputNumber({ v2, ...props }: { v2?: boolean; [key: string]: any }) {
    return v2 ? <InputNumberV2 {...props} /> : <InputNumberOld {...props} />;
}

export default InputNumber;
